import type { Timezone } from 'shared/lib/timezones';
import { timezoneOptions } from 'shared/lib/timezones';
import type { ReactNode } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import {
  type Days,
  type ContractorGetCampaignModel,
  type ManagerGetCampaignModel,
  type ScheduleModel,
  useUpdateCampaign,
} from 'entities/campaign';
import { AlertBox } from 'shared/ui/AlertBox';
import { AlertModal, SideModal, SideModalBody, SideModalFooter, useModalsActions } from 'shared/ui/SideModal';
import { isValidationErrorsModel } from 'shared/lib/api';
import { form } from 'shared/lib/form';
import { showSuccessToast } from 'shared/lib/notifications';
import { getKeys } from 'shared/lib/object';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

import { UpdateSchedule } from './UpdateSchedule';

const HIDDEN_ERROR = 'hidden';
const SUCCESS_MESSAGE = 'Leads receiving time was successfully updated.';

interface LeadsReceivingTimeModalProps {
  campaign: ManagerGetCampaignModel | ContractorGetCampaignModel;
  timeZone: Timezone;
  children: ReactNode;
}

export function LeadsReceivingTimeModal({ children, campaign, timeZone }: LeadsReceivingTimeModalProps) {
  if (!campaign.schedule) {
    throw new Error('Campaign schedule must be not null');
  }

  const methods = useForm({ defaultValues: campaign.schedule });
  const { control, setError, handleSubmit } = methods;
  const scheduleValue = useWatch({ control }) as ScheduleModel;
  const updateScheduleQuery = useUpdateCampaign(campaign.id);
  const { forceClose } = useModalsActions();
  const submit = handleSubmit((value) => {
    updateScheduleQuery.mutate(
      { payload: { schedule: value } },
      {
        onSuccess: () => {
          forceClose();
          showSuccessToast(SUCCESS_MESSAGE);
        },
        onError: (error) => {
          const errorData = error.response.data;

          if (!isValidationErrorsModel(errorData)) {
            return;
          }
          const errors = errorData.errors;

          for (const key of getKeys(errors)) {
            const day = key as Days;
            const error = errors[key];

            if (error) {
              setError(day, { message: error[0].message });
            }

            setError(`${day}.timeTo`, { message: HIDDEN_ERROR });
            setError(`${day}.timeFrom`, { message: HIDDEN_ERROR });
          }
        },
      }
    );
  });
  const timeZoneValue = timezoneOptions.find((x) => x.id === timeZone)?.text;

  return (
    <SideModal autoTestAttributesName="lead-delivery-schedule-modal">
      <SideModalBody title="Lead delivery schedule">
        <FormProvider {...methods}>
          <form onSubmit={submit}>
            <AlertModal
              autoTestAttributesName="lead-delivery-schedule-exit-modal"
              showCondition={form(methods)}
            />
            <AlertBox
              className="mb-4 border-deep-blue-100"
              iconClass="text-deep-blue-500"
              iconName="info"
              {...getDataAutoTestAttributes([`info-leads-receiving-time`])}
            >
              <div className="text-sm">
                <div className="font-medium">You may receive some leads outside of schedule</div>
                <div className="mt-2 text-slate-700">
                  The majority of leads will arrive during your chosen timeframe, though some may come at other times.
                </div>
              </div>
            </AlertBox>
            <div className="mb-4 text-lg font-semibold">Lead delivery schedule</div>
            <div className="mb-2 text-xs text-slate-700">{timeZoneValue}</div>
            {children}
          </form>
        </FormProvider>
      </SideModalBody>
      <SideModalFooter>
        <UpdateSchedule
          schedule={scheduleValue}
          isLoading={updateScheduleQuery.isLoading}
          onClick={submit}
        />
      </SideModalFooter>
    </SideModal>
  );
}
